import React, { useEffect, useState, memo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { updateCargoAssigner, saveStatusCargoAssigner } from 'actions/cargoes/assigners';
import { addNotification } from 'actions/notifications/notifications';
import { AutoComplete, IconButton, MenuItem, RaisedButton, SelectField, TableRow, TableRowColumn } from 'material-ui';
import _ from 'lodash';
import styles from 'styles';
import api from 'services';
import SettingsIcon from 'material-ui/svg-icons/action/settings';
import ContentRemoveIcon from 'material-ui/svg-icons/action/delete';
import ModifyIcon from 'material-ui/svg-icons/content/create';
import useDebounce from 'helpers/useDebounce';
import UpdateRealCargoDialog from './UpdateRealCargoDialog';
import { checkIfUpdateGazetter } from './utils';
import './CargoEditorRow.styles.scss';
import withClearButton from 'components/common/HOC/withClearButton';

const AutoCompleteWithClear = withClearButton(AutoComplete);


/**
 * @param {{
 *  updateCargoAssigner: (item: Item) => void,
 *  saveStatusCargoAssigner: (item: Item) => void,
 *  cargoTypes: any[],
 *  addNotification: (any) => void,
 *  updateRowsRealCargoData: (rows: Item[]) => void,
 *  cargoNameMatch: CargoNameMatch | null,
 *  item: Item,
 *  changePendingUpdates: (pendingUpdates: string) => void,
 *  updateCargoMatches: (cargoMatches: CargoNameMatch[]) => void,
 *  cargoNameMatches: CargoNameMatch[],
 *  updateRow: (row: Item) => void,
 * }} param0 
 * @returns 
 */
function CargoEditorRow({
    updateCargoAssigner,
    saveStatusCargoAssigner,
    cargoTypes,
    addNotification,
    updateRowsRealCargoData,
    cargoNameMatch,
    updateCargoMatches,
    cargoNameMatches,
    updateRow,
    ...props
}) {
    const _cargoCategoryNames = cargoTypes.map(item => ({
        name: item.name,
        item,
    }));

    const [item, setItem] = useState(props.item);
    const [realCargoNames, setRealCargoNames] = useState([]);
    const [cargoCategoryNames, _setCargoCategoryNames] = useState(_cargoCategoryNames);
    const [realCargoName, setRealCargoName] = useState(props.item.realCargoName);
    const [preventFirstSearch, setPreventFirstSearch] = useState(true);
    const [isModifyDialogOpen, setIsModifyDialogOpen] = useState(false);
    const [isSuggesting, setIsSuggesting] = useState(false);

    const hasSearchedRef = React.useRef(false);
    const isInitialRender = React.useRef(true);

    const debouncedSearchValue = useDebounce(realCargoName, 500);

    useEffect(() => {
        if (
            preventFirstSearch || 
            isInitialRender.current || // skip if the component is still initializing
            !debouncedSearchValue || 
            debouncedSearchValue.length < 2 ||
            !item._id ||
            // skip if the value is the initial value
            (!hasSearchedRef.current && debouncedSearchValue === props.item.realCargoName) ||
            // skip if the value is from a suggestion match
            (isSuggesting && 
                cargoNameMatch && 
                cargoNameMatch.suggestedCargo && 
                cargoNameMatch.suggestedCargo.realCargoName === debouncedSearchValue)
        ) {
            return;
        }

        hasSearchedRef.current = true;

        api.getCargoNamesAutocomplete(debouncedSearchValue)
            .then((response) => {
                const _realCargoNames = response.data.data.map(_realCargoName => ({
                    name: _realCargoName.name,
                    item: (<MenuItem
                        value={_realCargoName}
                        primaryText={_realCargoName.name}
                        secondaryText={
                            ['admin', 'moderator'].includes(_realCargoName.addedByRole) && 
                            <SettingsIcon 
                                style={{ 
                                    position: 'absolute', 
                                    left: -10, 
                                    marginTop: '11px',
                                    color: '#9E9E9E',
                                }} 
                            />
                        }
                        style={{ paddingLeft: '25px' }}
                    />),
                }));

                setRealCargoNames(_realCargoNames);
                document.body.scrollTop += 1;
                document.body.scrollTop -= 1;
            })
            .catch(() => null);
    }, [debouncedSearchValue]);

    useEffect(() => {
        props.changePendingUpdates(item.isUpdate ? '+' : '-');
    }, [item.isUpdate]);

    useEffect(() => {
        setItem(props.item);
        setRealCargoName(props.item.realCargoName);
    }, [props.item]);

    useEffect(() => {
        // longer initial delay to ensure everything is properly initialized
        const timer = setTimeout(() => {
            setPreventFirstSearch(false);
            isInitialRender.current = false; // Mark component as fully mounted
        }, 1500);
        
        return () => clearTimeout(timer);
    }, [props.item._id]);

    useEffect(() => {
        if (
            cargoNameMatch && cargoNameMatch.suggestedCargo) { 
            setIsSuggesting(true);

            const _item = _.cloneDeep(item);
            _item.realCargoName = cargoNameMatch.suggestedCargo.realCargoName;
            _item.realCargoId = cargoNameMatch.suggestedCargo.realCargoId;
            _item.details = cargoNameMatch.suggestedCargo.details;
            _item.CargoCategory = cargoNameMatch.suggestedCargo.CargoCategory;
            _item.CargoCategoryName = cargoNameMatch.suggestedCargo.CargoCategoryName;
            _item.isSuggest = true;

            setItem(_item);
            setRealCargoName(cargoNameMatch.suggestedCargo.realCargoName);
            saveStatusCargoAssigner(_item);
        } else {
            setIsSuggesting(false);
        }   
    }, [cargoNameMatch]);

    const onNewRequestRealCargoNames = (updateProperties) => {
        if (!updateProperties) {
            return;
        }

        const _item = _.cloneDeep(item);

        if (updateProperties.item.props.value.parent) {
            cargoTypes.forEach((cargoType) => {
                if (updateProperties.item.props.value.parent.red._id === cargoType._id) {
                    _item.CargoCategory = cargoType._id;
                    _item.CargoCategoryName = cargoType.name;
                }
            });
        }

        _item.realCargoName = updateProperties.name;
        _item.realCargoId = updateProperties.item.props.value._id;
        _item.details = updateProperties.item.props.value.details;
        _item.isUpdate = true;

        setItem(_item);
        setRealCargoNames([]);
        saveStatusCargoAssigner(_item);
    };

    const onUpdateInputCargoType = (searchText) => {
        const _item = _.cloneDeep(item);
        _item.CargoCategoryName = searchText;

        _item.isUpdate = checkIfUpdateGazetter(_item, cargoTypes);

        setItem(_item);

        saveStatusCargoAssigner(_item);
    };

    const onNewRequestCargoTypes = (updateProperties) => {
        if (!updateProperties.name) {
            return;
        }

        const _item = _.cloneDeep(item);

        _item.isUpdate = false;
        _item.isSuggest = false;
        _item.CargoCategoryName = updateProperties.name;
        _item.CargoCategory = updateProperties.item._id;
        _item.details = updateProperties.item.details;

        if (_item.realCargoName &&
            _item.realCargoName.length >= 3
        ) {
            _item.isUpdate = true;
        }

        setItem(_item);

        saveStatusCargoAssigner(_item);
    };

    const handleChangeDetail = (value) => {
        const _item = _.cloneDeep(item);
        _item.isUpdate = checkIfUpdateGazetter(_item, cargoTypes);
        _item.details = value;

        setItem(_item);

        saveStatusCargoAssigner(_item);
    };

    const handleUpdateAssigner = async () => {
        const updated = await api.updateCargoAssigner(item);
        if (updated.status === 200) {
            const _item = _.cloneDeep(updated.data);
            _item.isUpdate = false;
            _item.isSuggest = false;
            setItem(_item);
            addNotification({
                title: 'Success',
                message: 'Cargo updated',
                level: 'success',
                _id: updated.data._id,
                autoDismiss: 2,
            });
            setIsSuggesting(false);
            updateCargoMatches(cargoNameMatches.filter(match => match.query !== cargoNameMatch.query));
            updateRow(_item);
        } else {
            addNotification({
                title: 'Error',
                message: updated.errors[0].messages[0],
                level: 'error',
                _id: item,
                autoDismiss: 10,
            });
        }
    };

    return (
        <TableRow className={isSuggesting ? 'suggestingRow' : ''}>
            {
                isModifyDialogOpen &&
                <UpdateRealCargoDialog 
                    handleCloseDialog={setIsModifyDialogOpen}
                    open={isModifyDialogOpen}
                    item={item}
                    cargoTypes={cargoTypes}
                    updateRowsRealCargoData={updateRowsRealCargoData}
                />
            }
            <TableRowColumn
                style={{ ...styles.tableRowColumnCSS, position: 'relative' }}
                className='cargo-assigner_name_column'
            >
                <span className='name'>
                    {isSuggesting && (
                        <span className='matchIndicator'>
                            <span className='matchAsterisk'>*</span>
                        </span>
                    )}
                    {item.value}
                </span>
            </TableRowColumn>

            <TableRowColumn 
                style={{
                    ...styles.tableRowColumnCSS,
                    position: 'relative',
                }}
            >
                {isSuggesting && (
                    <div className='scoreIndicator'>
                        <span className='scoreBadge'>
                            {cargoNameMatch && cargoNameMatch.score ? 
                                `Match: ${Math.round(cargoNameMatch.score * 100)}%` : 
                                'Suggested'}
                        </span>
                    </div>
                )}
                <AutoCompleteWithClear
                    textFieldStyle={{
                        ...styles.autoCompleteCSS,
                        width: '100%',
                    }}
                    hintText='Real cargo name'
                    searchText={realCargoName || ''}
                    dataSource={realCargoNames}
                    dataSourceConfig={{ text: 'name', value: 'item' }}
                    onUpdateInput={(text) => {
                        setRealCargoName(text);

                        // only update the item state and trigger side effects if:
                        // 1. not in initial rendering phase
                        // 2. the component is fully mounted
                        // 3. user is actually typing (not just getting a suggestion)
                        if (!preventFirstSearch && !isInitialRender.current && text !== props.item.realCargoName) {
                            setItem({
                                ...item,
                                isUpdate: checkIfUpdateGazetter({ ...item, realCargoName: text }, cargoTypes),
                                isSuggest: false,
                                realCargoName: text,
                                realCargoId: undefined,
                            });
                            setIsSuggesting(false);
                        }
                    }}
                    onNewRequest={onNewRequestRealCargoNames}
                    filter={AutoComplete.noFilter}
                    listStyle={styles.autoCompleteListStyle}
                    popoverProps={{
                        canAutoPosition: true,
                        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
                        targetOrigin: { horizontal: 'left', vertical: 'top' },
                    }}
                    containerStyle={{ 
                        width: '100%',
                        position: 'relative',
                    }}
                    inputStyle={{
                        paddingRight: '30px',
                    }}
                    value={realCargoName}
                    clearValue={!!realCargoName}
                    onClear={() => {
                        setRealCargoName('');
                        setItem({
                            ...item,
                            realCargoName: '',
                            realCargoId: undefined,
                            CargoCategory: undefined,
                            CargoCategoryName: undefined,
                            details: undefined,
                            isUpdate: checkIfUpdateGazetter({ ...item, realCargoName: '' }, cargoTypes),
                            isSuggest: false,
                        });
                        setIsSuggesting(false);
                    }}
                    style={{ width: '100%' }}
                />
            </TableRowColumn>

            <TableRowColumn 
                style={styles.tableRowColumnCSS} 
                className={isSuggesting ? 'highlightedColumn' : ''}
            >
                <AutoComplete
                    textFieldStyle={styles.autoCompleteCSS}
                    hintText='Cargo type'
                    searchText={item.CargoCategoryName || ''}
                    dataSource={cargoCategoryNames}
                    dataSourceConfig={{ text: 'name', value: 'item' }}
                    onUpdateInput={onUpdateInputCargoType}
                    onNewRequest={onNewRequestCargoTypes}
                    filter={AutoComplete.caseInsensitiveFilter}
                    listStyle={styles.autoCompleteListStyle}
                    popoverProps={{
                        canAutoPosition: true,
                    }}
                    disabled={!!item.realCargoId}
                    underlineDisabledStyle={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}
                />
            </TableRowColumn>

            <TableRowColumn style={styles.tableRowColumnCSS}>
                {_.has(item, 'lastModify.date') ? new Date(item.lastModify.date).toLocaleString() : ''}
            </TableRowColumn>

            <TableRowColumn 
                style={styles.tableRowColumnSmallCSS}
            >
                <SelectField
                    style={styles.selectFieldCSS}
                    value={item.details}
                    onChange={(_e, _i, v) => handleChangeDetail(v)}
                    maxHeight={200}
                    autoWidth
                    disabled={!!item.realCargoId}
                    underlineDisabledStyle={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}
                >
                    {
                        [...new Array(15)].map((_un, i) => <MenuItem key={`details-${i}`} style={styles.menuItemCSS} value={i + 1} primaryText={i + 1} />)
                    }
                </SelectField>
            </TableRowColumn>

            <TableRowColumn style={styles.tableRowColumnCSS}>
                {_.has(item, 'lastModify.user.email') ? item.lastModify.user.email : ''}
            </TableRowColumn>

            <TableRowColumn style={styles.tableRowColumnSmallCSS}>
                <div className={item.isUpdate || item.isSuggest ? 'update-item' : 'hidden update-item'}>
                    <RaisedButton label='Update' primary onClick={handleUpdateAssigner} />
                </div>
            </TableRowColumn>
            <TableRowColumn style={{ ...styles.tableRowColumnSmallCSS, width: '48px' }}>
                {
                    item.realCargoId && !item.isUpdate ?
                        <IconButton>
                            <ModifyIcon
                                onClick={() => setIsModifyDialogOpen(!isModifyDialogOpen)}
                                color='#eaeaea'
                                hoverColor='#00BCD4'
                            />
                        </IconButton> : 
                        null
                }
            </TableRowColumn>
            <TableRowColumn style={{ ...styles.tableRowColumnSmallCSS, width: '48px' }}>
                <IconButton>
                    <ContentRemoveIcon
                        onClick={() => props.removeGazetter(item._id)}
                        color='#eaeaea'
                        hoverColor='red'
                    />
                </IconButton>
            </TableRowColumn>
        </TableRow>
    );
}

export default memo(connect(
    (state, ownProps) => {
        const { cargoTypes } = state;
        const { item } = ownProps;

        return {
            cargoTypes,
            item,
        };
    },
    dispatch => bindActionCreators(
        {
            updateCargoAssigner,
            saveStatusCargoAssigner,
            addNotification,
        },
        dispatch,
    ),
)(CargoEditorRow));

/**
 * item
 * @typedef {Object} Item
 * @property {string} _id
 * @property {number} details
 * @property {string} value
 * @property {string} [realCargoId]
 * @property {string} realCargoName
 * @property {boolean} [isUpdate]
 * @property {boolean} [isSuggest]
 * @property {string} CargoCategory
 * @property {string} CargoCategoryName
 * @property {string} [lastModify.date]
 * @property {string} [lastModify.user.email]
 */

/**
 * @typedef {Object} SuggestedCargoData
 * @property {string} realCargoName - The suggested real cargo name
 * @property {string} realCargoId - The unique ID of the suggested cargo in the database
 * @property {number} details - The details level for the cargo (1-15)
 * @property {string} CargoCategory - The unique ID of the cargo category
 * @property {string} CargoCategoryName - The human-readable name of the cargo category
 */

/** 
 * @typedef {Object} CargoNameMatch
 * @property {string} query
 * @property {string} match
 * @property {number} score
 * @property {number} details
 * @property {string} cargoType
 * @property {SuggestedCargoData} suggestedCargo
 */



// WEBPACK FOOTER //
// ./app/components/Cargoes/Assigners/CargoEditorRow.jsx